import { useEffect, useRef, RefObject } from "react"

interface UseDragAutoScrollOptions {
  direction?: "horizontal" | "vertical" | "both"
  threshold?: number
  maxSpeed?: number
}

interface UseDragAutoScrollParams {
  scrollContainerRef: RefObject<HTMLElement | null>
  isDragging: boolean
  clientX: number | null
  clientY: number | null
  options?: UseDragAutoScrollOptions
}

export const useDragAutoScroll = ({
  scrollContainerRef,
  isDragging,
  clientX,
  clientY,
  options = {},
}: UseDragAutoScrollParams) => {
  const { direction = "horizontal", threshold = 60, maxSpeed = 15 } = options

  const animationFrameRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isDragging || clientX === null || clientY === null) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      return
    }

    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    const scroll = () => {
      if (!scrollContainerRef.current) return

      const rect = scrollContainerRef.current.getBoundingClientRect()
      let scrollX = 0
      let scrollY = 0

      if (direction === "horizontal" || direction === "both") {
        const distanceFromLeft = clientX - rect.left
        const distanceFromRight = rect.right - clientX

        if (distanceFromLeft < 0) {
          // Cursor is outside the container on the left - scroll at max speed
          scrollX = -maxSpeed
        } else if (distanceFromRight < 0) {
          // Cursor is outside the container on the right - scroll at max speed
          scrollX = maxSpeed
        } else if (distanceFromLeft < threshold) {
          // Cursor is in the left threshold zone - accelerate based on proximity
          const intensity = 1 - distanceFromLeft / threshold
          scrollX = -maxSpeed * intensity * intensity
        } else if (distanceFromRight < threshold) {
          // Cursor is in the right threshold zone - accelerate based on proximity
          const intensity = 1 - distanceFromRight / threshold
          scrollX = maxSpeed * intensity * intensity
        }
      }

      if (direction === "vertical" || direction === "both") {
        const distanceFromTop = clientY - rect.top
        const distanceFromBottom = rect.bottom - clientY

        if (distanceFromTop < 0) {
          // Cursor is outside the container on the top - scroll at max speed
          scrollY = -maxSpeed
        } else if (distanceFromBottom < 0) {
          // Cursor is outside the container on the bottom - scroll at max speed
          scrollY = maxSpeed
        } else if (distanceFromTop < threshold) {
          // Cursor is in the top threshold zone - accelerate based on proximity
          const intensity = 1 - distanceFromTop / threshold
          scrollY = -maxSpeed * intensity * intensity
        } else if (distanceFromBottom < threshold) {
          // Cursor is in the bottom threshold zone - accelerate based on proximity
          const intensity = 1 - distanceFromBottom / threshold
          scrollY = maxSpeed * intensity * intensity
        }
      }

      if (scrollX !== 0 || scrollY !== 0) {
        scrollContainerRef.current.scrollLeft += scrollX
        scrollContainerRef.current.scrollTop += scrollY
      }

      animationFrameRef.current = requestAnimationFrame(scroll)
    }

    animationFrameRef.current = requestAnimationFrame(scroll)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [isDragging, clientX, clientY, direction, threshold, maxSpeed, scrollContainerRef])
}
